import React, { useState, useEffect } from "react";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import GroupIcon from "@material-ui/icons/Group";
import PersonIcon from "@material-ui/icons/Person";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getGroupDetails } from "../../services/groupService";

const useStyles = makeStyles((theme) => ({
  groupHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  groupAvatar: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    marginBottom: theme.spacing(2),
  },
  infoSection: {
    marginBottom: theme.spacing(2),
  },
  infoLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  participantsList: {
    maxHeight: 300,
    overflow: "auto",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  adminChip: {
    marginLeft: theme.spacing(1),
  },
}));

const GroupInfoModal = ({ open, onClose, group }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    if (open && group) {
      loadGroupMetadata();
    }
  }, [open, group]);

  const loadGroupMetadata = async () => {
    setLoading(true);
    try {
      const data = await getGroupDetails(group.id, group.whatsappId);
      setMetadata(data.group);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("groupManagement.infoModal.title")}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : metadata ? (
          <>
            <div className={classes.groupHeader}>
              <Avatar
                src={metadata.profilePicUrl}
                className={classes.groupAvatar}
              >
                <GroupIcon style={{ fontSize: 60 }} />
              </Avatar>
              <Typography variant="h6">{metadata.name}</Typography>
            </div>

            <div className={classes.infoSection}>
              <Typography className={classes.infoLabel}>
                {i18n.t("groupManagement.infoModal.fields.description")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {metadata.description ||
                  i18n.t("groupManagement.infoModal.noDescription")}
              </Typography>
            </div>

            {metadata.createdAt && (
              <div className={classes.infoSection}>
                <Typography className={classes.infoLabel}>
                  {i18n.t("groupManagement.infoModal.fields.createdAt")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {format(parseISO(metadata.createdAt), "dd/MM/yyyy HH:mm")}
                </Typography>
              </div>
            )}

            <div className={classes.infoSection}>
              <Typography className={classes.infoLabel}>
                {i18n.t("groupManagement.infoModal.fields.participants")} (
                {metadata.participants?.length || 0})
              </Typography>
              <List className={classes.participantsList}>
                {metadata.participants?.map((participant) => (
                  <ListItem key={participant.id}>
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={participant.name || participant.id}
                      secondary={participant.id}
                    />
                    {participant.isAdmin && (
                      <Chip
                        label={i18n.t(
                          "groupManagement.infoModal.fields.admins"
                        )}
                        size="small"
                        color="primary"
                        className={classes.adminChip}
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            </div>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {i18n.t("groupManagement.infoModal.buttons.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupInfoModal;
